
import { ApolloCache } from '@apollo/client';
import { getSafe } from 'helpers';
import { isString as _isString } from 'lodash-es';


export type FieldAndArgsTest = (fieldAndArgs: { fieldName: string; args: Record<string, any> }) => boolean;

/**
 * This helper function evicts items in the ROOT_QUERY of the
 * cache if the predicate which accepts the fieldKey and the keyArgs
 * returns true
 *
 * @param cache Apollo Cache Client
 * @param fieldAndArgsTest: Prediciate to check
 */
export const invalidateApolloCacheFor = (
  cache: ApolloCache<any>,
  fieldAndArgsTest: FieldAndArgsTest
) => {

  // Extracts all keys on the root query
  const rootQueryKeys = Object.keys(cache.extract().ROOT_QUERY);

  const itemsToEvict = rootQueryKeys
    .map(key => extractFieldNameAndArgs(key))
    .filter(r => fieldAndArgsTest(r));

  itemsToEvict.forEach(({ fieldName, args }) => {
    cache.evict(
      {
        id: 'ROOT_QUERY',
        fieldName,
        args
      }
    );
  });
};


/**
 * Extracts the field name and the keyArgs
 * from an Apollo cache key.
 * eg: searchTags:{"keyword":"F"} =>
 * returns { fieldName: 'searchTags', args: { keyword: "F" }  }
 *
 * @param storeFieldName key in the apollo cache.
 */
export const extractFieldNameAndArgs = (storeFieldName: string) => {
  const fieldName = extractFieldName(storeFieldName);
  const args = convertKeyArgs(storeFieldName);

  return { fieldName, args };
};

const extractFieldName = (storeFieldName: string) => {
  const regexp = new RegExp(/([\w])+/g);
  return getSafe(() => storeFieldName.match(regexp)[0], storeFieldName);
};

// Convert the keyArgs stored as a string in the query key
// to a json object to be used for the args when evicting from the cache
const convertKeyArgs = (key: string): Record<string, any> => {
  const startArgsIndex = key.indexOf('{');
  const endArgsIndex = key.lastIndexOf('}');
  if (startArgsIndex === -1) {
    return null;
  }
  if (startArgsIndex === endArgsIndex) {
    return null;
  }

  const keyArgsString = key.substring(
    startArgsIndex,
    endArgsIndex + 1
  );

  // We should have a string here that can be parsed to JSON, or null
  // getSafe is an internal helper function that wraps a try catch
  const args = getSafe(() => JSON.parse(keyArgsString), null);
  return args;
};
